import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProjectMessages from './ProjectMessages';
import ProjectSchedule from './ProjectSchedule';
import ProjectFiles from './ProjectFiles';
import InviteCollaboratorModal from './InviteCollaboratorModal';

const CommunicationCentre = ({ projectIdProp = null }) => {
  const params = useParams();
  const navigate = useNavigate();
  // Handle both routes: /plan/:id/communication and /shared/:token/communication
  // If projectIdProp is provided (from modal), use it instead of params
  const projectId = projectIdProp || params.id; // For designer route
  const shareToken = params.token && !params.id ? params.token : null; // For shared route
  const [project, setProject] = useState(null);
  const [projectIdFromShare, setProjectIdFromShare] = useState(null);
  const [shareInfo, setShareInfo] = useState(null); // Store share info (share_type, schedule_access)
  const [designerInfo, setDesignerInfo] = useState(null); // { id, name, email } for shared-link UI clarity
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages'); // 'messages', 'schedule', 'activity', 'collaborators', 'files'
  const [composeRequest, setComposeRequest] = useState(null); // { text, nonce }
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [invitedCollaborators, setInvitedCollaborators] = useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [notificationMethod, setNotificationMethod] = useState('email');
  const [inviteType, setInviteType] = useState('trade');
  const [tradeCategory, setTradeCategory] = useState('contractor'); // Category for trades/suppliers
  const [messageVisibility, setMessageVisibility] = useState('all'); // 'all', 'selected', 'none'
  const [scheduleAccess, setScheduleAccess] = useState(true); // For invite modal
  
  // Trade share-link UX: help trades save access on their device
  const TRADE_SAVED_PROJECTS_KEY = 'tradeSavedProjectsV1';
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]); // [{ token, url, projectName, designerName, savedAt }]
  const [copied, setCopied] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // brief inline feedback

  // Google Business search state
  const [inviteMode, setInviteMode] = useState('manual'); // 'manual' or 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [designerLocation, setDesignerLocation] = useState(null);
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    businessType: '',
    address: '',
    city: '',
    provinceState: '',
    phone: '',
    email: '',
    website: ''
  });
  
  // Get the actual project ID to use
  const actualProjectId = projectId || projectIdFromShare || project?.id;
  const isTradeShareViewer = !!shareToken && (shareInfo?.share_type || '').toString().toLowerCase() === 'trade';
  const shareUrl = shareToken ? `${window.location.origin}/shared/${shareToken}/communication` : null;

  const readSavedProjects = () => {
    try {
      const raw = localStorage.getItem(TRADE_SAVED_PROJECTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeSavedProjects = (next) => {
    const safe = Array.isArray(next) ? next : [];
    try {
      localStorage.setItem(TRADE_SAVED_PROJECTS_KEY, JSON.stringify(safe.slice(0, 25)));
    } catch {
      // ignore (storage full / blocked)
    }
    setSavedProjects(safe.slice(0, 25));
  };

  const isSavedOnDevice =
    !!shareToken && savedProjects.some((p) => p && (p.token === shareToken || p.url === shareUrl));

  const copyToClipboard = async (text) => {
    const t = (text || '').toString();
    if (!t) return false;

    // Modern clipboard API
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(t);
        return true;
      }
    } catch {
      // fall back
    }

    // Fallback (older browsers)
    try {
      const el = document.createElement('textarea');
      el.value = t;
      el.setAttribute('readonly', 'true');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return !!ok;
    } catch {
      return false;
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    const ok = await copyToClipboard(shareUrl);
    setCopied(ok);
    setSaveStatus(ok ? 'Link copied.' : 'Could not copy. Long-press and copy from the address bar.');
    window.setTimeout(() => setCopied(false), 1200);
    window.setTimeout(() => setSaveStatus(''), 2500);
  };

  const handleSaveProject = async () => {
    if (!shareToken || !shareUrl) return;
    const projectName = project?.name || 'Project';
    const designerName = designerInfo?.name || null;

    const current = readSavedProjects();
    const without = current.filter((p) => p && p.token !== shareToken && p.url !== shareUrl);
    const next = [
      { token: shareToken, url: shareUrl, projectName, designerName, savedAt: new Date().toISOString() },
      ...without
    ];
    writeSavedProjects(next);

    // Copy link as a helpful default (so they can paste into Notes/SMS too)
    await handleCopyLink();
    setSaveStatus('Saved on this device.');
    window.setTimeout(() => setSaveStatus(''), 2500);
  };

  const handleRemoveSaved = (tokenOrUrl) => {
    const current = readSavedProjects();
    const next = current.filter((p) => p && p.token !== tokenOrUrl && p.url !== tokenOrUrl);
    writeSavedProjects(next);
    setSaveStatus('Removed.');
    window.setTimeout(() => setSaveStatus(''), 2000);
  };

  const requestScheduleChangeViaChat = (kind = 'change') => {
    const header = kind === 'new' ? 'Schedule request (new item):' : 'Schedule request (change):';
    const text =
      `${header}\n` +
      `- Dates/time(s):\n` +
      `- How long onsite (each visit):\n` +
      `- What work you’ll be doing:\n` +
      `- Anything blocking you?\n`;
    // IMPORTANT: direct message to designer + this trade only (NOT "everyone")
    const email = shareInfo?.recipient_email;
    const name = shareInfo?.recipient_name;
    setComposeRequest({
      text,
      nonce: Date.now(),
      recipientsOverride: email ? [{ type: 'trade', email, name: name || email }] : null
    });
    setActiveTab('messages');
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  useEffect(() => {
    loadProject();
    if (activeTab === 'activity' && actualProjectId) {
      loadActivity();
    }
    if (activeTab === 'collaborators' && !shareToken && actualProjectId) {
      loadInvitedCollaborators();
    }
    loadDesignerLocation();
  }, [projectId, shareToken, activeTab, actualProjectId]);

  useEffect(() => {
    // Load saved projects for trade share viewers (per device).
    if (!isTradeShareViewer) return;
    setSavedProjects(readSavedProjects());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTradeShareViewer, shareToken]);

  // Redirect trades without schedule access away from schedule tab
  useEffect(() => {
    if (shareInfo?.share_type === 'trade' && shareInfo?.schedule_access === false && activeTab === 'schedule') {
      setActiveTab('messages');
    }
    // Redirect trades away from activity tab
    if (shareInfo?.share_type === 'trade' && activeTab === 'activity') {
      setActiveTab('messages');
    }
  }, [shareInfo, activeTab]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/collaboration/shared/${shareToken}`
        : `${API_BASE_URL}/projects/${projectId}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load project');
      const data = await response.json();
      
      if (shareToken) {
        setProject(data.project);
        setProjectIdFromShare(data.project?.id); // Store project ID from share
        setShareInfo(data.share); // Store share info (share_type, schedule_access)
        setDesignerInfo(data.designer || null);

        // Clients use the plan view for Client Chat (Design Feedback).
        const st = (data.share?.share_type || 'client').toString().toLowerCase();
        if (st !== 'trade') {
          navigate(`/shared/${shareToken}`);
          return;
        }
      } else {
        setProject(data.project || data);
        setShareInfo(null); // Designer view, no share info
        setDesignerInfo(null);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      setLoadingActivity(true);
      const authToken = localStorage.getItem('authToken');
      let url, headers = { 'Content-Type': 'application/json' };
      
      const pid = actualProjectId;
      if (!pid) return;
      
      if (shareToken) {
        url = `${API_BASE_URL}/collaboration/activity/${pid}?shareToken=${shareToken}`;
      } else {
        url = `${API_BASE_URL}/collaboration/activity/${pid}`;
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load activity');
      const data = await response.json();
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Error loading activity:', error);
      setActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const loadInvitedCollaborators = async () => {
    if (shareToken) return; // Only designers can see the list
    
    try {
      setLoadingCollaborators(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const pid = actualProjectId;
      if (!pid) return;

      // Get both clients and trades
      const [clientsRes, tradesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/collaboration/shares/${pid}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_BASE_URL}/collaboration/trades/${pid}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);

      // Get all shares (needed to match with trades and filter clients)
      const allSharesRes = await fetch(`${API_BASE_URL}/collaboration/shares/${pid}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const allShares = allSharesRes.ok ? (await allSharesRes.json()).shares || [] : [];
      
      const trades = tradesRes.ok ? (await tradesRes.json()).trades || [] : [];
      
      // Filter clients: only shares that are NOT trades
      const clientShares = allShares.filter(s => s.share_type !== 'trade');
      
      // Get trade share tokens to avoid duplicates
      const tradeShareTokens = new Set(trades.map(t => t.share_token));

      const collaborators = [
        // Clients: only shares that are not trades
        ...clientShares
          .filter(c => !tradeShareTokens.has(c.share_token)) // Also exclude if it's a trade share token
          .map(c => ({
            ...c,
            type: 'client',
            shareToken: c.share_token,
            id: c.id, // Share ID for deletion
            message_visibility: c.message_visibility || 'all', // Include message visibility
            schedule_access: c.schedule_access !== undefined ? c.schedule_access : true // Include schedule access
          })),
        // Trades: match with their shares (need to query shares including trades)
        ...trades
          .map(t => {
            // Find the matching share for this trade - need to check allShares which may not include trades
            // So we need to query for the share separately if not found
            let matchingShare = allShares.find(s => s.share_token === t.share_token);
            
            // If not found in allShares, try to find it in a separate query for trade shares
            if (!matchingShare && t.share_token) {
              // The share should exist - try to find it by querying project_shares directly
              // For now, we'll create a placeholder - the actual share should be fetched
              matchingShare = {
                id: null, // Will need to be fetched
                share_token: t.share_token,
                message_visibility: 'all',
                schedule_access: true
              };
            }
            
            if (!matchingShare) {
              console.warn(`No matching share found for trade ${t.trade_name} with token ${t.share_token}`);
              return null; // Skip trades without matching shares
            }
            
            return {
              ...t,
              type: 'trade',
              recipient_name: t.trade_name,
              recipient_email: t.trade_email,
              shareToken: t.share_token,
              id: matchingShare.id, // Use share ID for deletion/updates
              message_visibility: matchingShare.message_visibility || 'all', // Include message visibility
              schedule_access: matchingShare.schedule_access !== undefined ? matchingShare.schedule_access : true // Include schedule access
            };
          })
          .filter(t => t !== null) // Remove null entries
      ];

      setInvitedCollaborators(collaborators);
    } catch (error) {
      console.error('Error loading collaborators:', error);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const handleUpdateScheduleAccess = async (collab, newAccess) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('You must be logged in to update settings');
        return;
      }

      if (!collab.id) {
        alert('Cannot update settings - share ID not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/collaboration/share/${collab.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scheduleAccess: newAccess
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update schedule access');
      }
      
      // Refresh the list
      loadInvitedCollaborators();
      
      alert(`Schedule access ${newAccess ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating schedule access:', error);
      alert(`Failed to update schedule access: ${error.message}`);
    }
  };

  const handleUpdateMessageVisibility = async (collab, newVisibility) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('You must be logged in to update settings');
        return;
      }

      if (!collab.id) {
        alert('Cannot update settings - share ID not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/collaboration/share/${collab.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageVisibility: newVisibility
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update message visibility');
      }
      
      // Refresh the list
      loadInvitedCollaborators();
      
      alert(`Message visibility updated to "${newVisibility === 'all' ? 'All Messages' : newVisibility === 'selected' ? 'Selected Conversations' : 'No Past Messages'}"`);
    } catch (error) {
      console.error('Error updating message visibility:', error);
      alert(`Failed to update message visibility: ${error.message}`);
    }
  };

  const handleDeleteCollaborator = async (collab) => {
    if (!window.confirm(`Are you sure you want to remove ${collab.recipient_name}? They will no longer be able to access this project.`)) {
      return;
    }

    if (!collab.id) {
      alert('Cannot remove this collaborator - share ID not found. Please refresh the page and try again.');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('You must be logged in to remove collaborators');
        return;
      }

      // Delete the share (which revokes access)
      const response = await fetch(`${API_BASE_URL}/collaboration/share/${collab.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to remove collaborator');
      }
      
      // Refresh the list
      loadInvitedCollaborators();
      
      alert(`${collab.recipient_name} has been removed from the project.`);
    } catch (error) {
      console.error('Error removing collaborator:', error);
      alert(`Failed to remove collaborator: ${error.message}`);
    }
  };

  const handleInvite = async (projectId) => {
    if (!inviteName.trim() || !inviteEmail.trim()) {
      alert('Please enter both name and email');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to invite collaborators');
        return;
      }

      const pid = projectId || actualProjectId;
      if (!pid) {
        alert('Project ID not found');
        return;
      }

      const url = inviteType === 'trade'
        ? `${API_BASE_URL}/collaboration/invite-trade`
        : `${API_BASE_URL}/collaboration/share`;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const body = inviteType === 'trade'
        ? {
            projectId: pid,
            tradeEmail: inviteEmail,
            tradeName: inviteName,
            tradeType: tradeCategory,
            phoneNumber: invitePhone || null,
            notificationMethod: notificationMethod || 'email',
            accessLevel: 'comment',
            message: 'You have been invited to collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: scheduleAccess
          }
        : {
            projectId: pid,
            recipientEmail: inviteEmail,
            recipientName: inviteName,
            phoneNumber: invitePhone || null,
            notificationMethod: notificationMethod || 'email',
            accessLevel: 'comment',
            message: 'You have been invited to view and collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: true // Clients always have schedule access
          };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to invite collaborator';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          
          // Handle specific error cases
          if (errorData.existingShare) {
            errorMessage = 'This collaborator is already invited to this project.';
          } else if (response.status === 400) {
            errorMessage = errorData.error || 'Invalid request. Please check all fields are filled correctly.';
          } else if (response.status === 403) {
            errorMessage = 'You do not have permission to invite to this project.';
          } else if (response.status === 404) {
            errorMessage = 'Project not found.';
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      const shareToken = data.share?.shareToken || data.trade?.share_token || data.share_token;
      const shareUrl =
        inviteType === 'trade'
          ? `${window.location.origin}/shared/${shareToken}/communication`
          : `${window.location.origin}/shared/${shareToken}`;
      
      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setNotificationMethod('email');
      setInviteType('trade');
      setTradeCategory('contractor');
      setMessageVisibility('all');
      setScheduleAccess(true);
      setShowInviteModal(false);
      
      // Show success modal with link and email option
      const emailBody = `Hi ${inviteName},\n\nYou have been invited to collaborate on a project.\n\nPlease click the link below to access:\n\n${shareUrl}\n\nThank you!`;
      const mailtoLink = `mailto:${inviteEmail}?subject=Project Collaboration Invitation&body=${encodeURIComponent(emailBody)}`;
      
      const copyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      };
      
      const openEmail = () => {
        window.location.href = mailtoLink;
      };
      
      const userChoice = window.confirm(
        `Invitation created!\n\n` +
        `Link: ${shareUrl}\n\n` +
        `Click OK to open your email client with the invitation, or Cancel to just copy the link.`
      );
      
      if (userChoice) {
        openEmail();
      } else {
        copyLink();
      }
      
      // Refresh the collaborators list
      loadInvitedCollaborators();
    } catch (error) {
      console.error('Error inviting collaborator:', error);
      const errorMessage = error.message || 'Failed to invite collaborator. Please try again.';
      alert(errorMessage);
    }
  };

  // Load designer location for default search area
  const loadDesignerLocation = async () => {
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Try to get location from profile
        try {
          const response = await fetch(`${API_BASE_URL}/designers/profile`, {
            headers: {
              ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
            }
          });
          if (response.ok) {
            const profile = await response.json();
            const location = [];
            if (profile.city) location.push(profile.city);
            if (profile.provinceState) location.push(profile.provinceState);
            if (profile.country) location.push(profile.country);
            if (location.length > 0) {
              setDesignerLocation(location.join(', '));
              setSearchLocation(location.join(', '));
            }
          }
        } catch (err) {
          // Fallback to user data
          if (user.city) {
            setDesignerLocation(user.city);
            setSearchLocation(user.city);
          }
        }
      }
    } catch (error) {
      console.error('Error loading designer location:', error);
    }
  };

  // Search Google Business
  const handleSearchBusinesses = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      alert('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        type: searchType
      });
      
      if (searchLocation) {
        params.append('location', searchLocation);
      }

      const response = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
        headers: {
          ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // If Google Places not configured, offer AI fallback
        if (errorData.error && errorData.error.includes('Google Places API key not configured')) {
          const useAI = window.confirm(
            'Google Places API is not set up.\n\n' +
            'Would you like to use AI to suggest businesses instead?\n\n' +
            'Note: AI suggestions won\'t include contact information - you\'ll need to find that manually.'
          );
          
          if (useAI) {
            params.append('useAI', 'true');
            const aiResponse = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
              headers: {
                ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
              }
            });
            
            if (aiResponse.ok) {
              const aiData = await aiResponse.json();
              setSearchResults(aiData.results || []);
              if (aiData.note) {
                alert(aiData.note);
              }
              return;
            }
          }
        }
        
        throw new Error(errorData.error || 'Search failed');
      }

      const data = await response.json();
      setSearchResults(data.results || []);
      
      // Don't show alert - let the UI handle empty state with helpful message
    } catch (error) {
      console.error('Error searching businesses:', error);
      alert(`Search failed: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  // Invite from search result
  const handleInviteFromSearch = async (business) => {
    // Use actual email if available, otherwise try to construct from website or use placeholder
    let email = business.email || '';
    
    if (!email && business.website) {
      // Try to construct email from website domain
      const domain = business.website.replace(/^https?:\/\//, '').split('/')[0];
      email = `contact@${domain}`;
    }
    
    // If still no email, use a placeholder that user must update
    if (!email) {
      email = ''; // Leave empty so user must fill it
    }
    
    setInviteName(business.name);
    setInviteEmail(email);
    setInviteType('trade');
    
    // Set category based on business type from search, or default to contractor
    if (business.business_type || business.type) {
      setTradeCategory(business.business_type || business.type || 'contractor');
    } else if (searchType && searchType !== 'all') {
      setTradeCategory(searchType);
    } else {
      setTradeCategory('contractor');
    }
    
    setInviteMode('manual'); // Switch to manual tab
    // Don't clear search results - let user see what they selected
    // Don't clear search query - keep it for reference
    
    // Ensure invite modal is open
    setShowInviteModal(true);
    
    // If email is missing, show a helpful message
    if (!email && !business.email) {
      // Small delay to ensure modal is visible
      setTimeout(() => {
        alert(`Business: ${business.name}\n\nEmail not found. Please enter the email address manually, or use the "Open Email" button to compose an email.`);
      }, 100);
    }
  };

  const handleAddBusiness = async (businessData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/google-business/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(businessData)
      });

      if (!response.ok) {
        throw new Error('Failed to add business');
      }

      alert('Business added! It will now appear in search results.');
      
      // Refresh search if there was a query
      if (searchQuery.trim()) {
        setSearching(true);
        try {
          const params = new URLSearchParams({
            query: searchQuery,
            type: searchType
          });
          
          if (searchLocation) {
            params.append('location', searchLocation);
          }

          const refreshResponse = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
            headers: {
              ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
            }
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setSearchResults(refreshData.results || []);
          }
        } catch (err) {
          console.error('Error refreshing search:', err);
        } finally {
          setSearching(false);
        }
      }
    } catch (error) {
      alert('Failed to add business. Please try again.');
      console.error('Error adding business:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-Optimized Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">{project?.name || 'Project'}</h1>
              <p className="text-sm text-gray-600">Collaborate</p>
            </div>
            <div className="ml-3 flex items-center gap-2">
              {isTradeShareViewer && (
                <button
                  onClick={() => setShowSavePanel((v) => !v)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 ${
                    isSavedOnDevice
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                      : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'
                  }`}
                  title="Save this project so you don’t lose it"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z" />
                  </svg>
                  {isSavedOnDevice ? 'Saved' : 'Save'}
                </button>
              )}
              <button
                onClick={() => {
                  if (shareToken) {
                    navigate(`/shared/${shareToken}`);
                  } else if (actualProjectId) {
                    navigate(`/plan/${actualProjectId}`);
                  }
                }}
                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View Plan
              </button>
            </div>
          </div>

          {/* Trade share link: save panel (copy link + add-to-home-screen guidance + saved list) */}
          {isTradeShareViewer && showSavePanel && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    Don’t lose this project link
                  </p>
                  <p className="text-xs text-amber-900/80 mt-0.5">
                    Save it to your phone so you can come back later (even if you lose the text message).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSavePanel(false)}
                  className="shrink-0 text-amber-900/70 hover:text-amber-900 px-2 py-1 rounded"
                  aria-label="Close"
                  title="Close"
                >
                  ✕
                </button>
              </div>

              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-4 py-2 bg-white border border-amber-300 text-amber-900 rounded-lg text-sm font-semibold hover:bg-amber-100"
                >
                  {copied ? 'Copied' : 'Copy Link'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveProject}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700"
                >
                  Save on this device
                </button>
              </div>

              {saveStatus && (
                <div className="mt-2 text-xs text-amber-900/90">
                  {saveStatus}
                </div>
              )}

              <details className="mt-3">
                <summary className="cursor-pointer text-sm font-semibold text-amber-900">
                  How to add to Home Screen / Bookmarks
                </summary>
                <div className="mt-2 text-xs text-amber-900/90 space-y-2">
                  <div>
                    <div className="font-semibold">iPhone / iPad (Safari)</div>
                    <div>Tap the Share icon → “Add to Home Screen”.</div>
                  </div>
                  <div>
                    <div className="font-semibold">Android (Chrome)</div>
                    <div>Tap ⋮ menu → “Add to Home screen”.</div>
                  </div>
                  <div>
                    <div className="font-semibold">Desktop</div>
                    <div>Press Ctrl+D (Windows) to bookmark this page.</div>
                  </div>
                </div>
              </details>

              {savedProjects.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-2">
                    Saved on this device
                  </div>
                  <div className="space-y-2">
                    {savedProjects.slice(0, 6).map((p) => (
                      <div
                        key={p.url || p.token}
                        className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2"
                      >
                        <button
                          type="button"
                          onClick={() => p.url && window.location.assign(p.url)}
                          className="text-left min-w-0 flex-1"
                          title="Open saved project"
                        >
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {p.projectName || 'Project'}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {p.designerName ? `Designer: ${p.designerName}` : (p.url || '')}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveSaved(p.token || p.url)}
                          className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-red-100 text-red-700 rounded hover:bg-red-200"
                          title="Remove"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 -mx-4 px-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'messages'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              💬 Trades Chat
            </button>
            {/* Collaborators tab - show first for designers */}
            {!shareToken && (
              <button
                onClick={() => {
                  setActiveTab('collaborators');
                  if (invitedCollaborators.length === 0) loadInvitedCollaborators();
                }}
                className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'collaborators'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                👥 Collaborators
              </button>
            )}
            {/* Schedule tab - hide for trades without schedule access */}
            {!(shareInfo?.share_type === 'trade' && shareInfo?.schedule_access === false) && (
              <button
                onClick={() => setActiveTab('schedule')}
                className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'schedule'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📅 Schedule
              </button>
            )}
            {/* Activity tab - hide for trades */}
            {shareInfo?.share_type !== 'trade' && (
              <button
                onClick={() => {
                  setActiveTab('activity');
                  if (activity.length === 0) loadActivity();
                }}
                className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'activity'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📋 Activity
              </button>
            )}
            <button
              onClick={() => setActiveTab('files')}
              className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'files'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📁 Files
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className={`max-w-4xl mx-auto ${activeTab === 'messages' ? 'px-0 sm:px-4 py-0 sm:py-4' : 'px-4 py-4'}`}>
        {activeTab === 'messages' && (
          <div className="bg-white sm:rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
              <p className="text-[10px] sm:text-xs text-blue-800 flex items-center gap-1.5 font-medium">
                <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span>
                Trades and supplier coordination.
              </p>
            </div>
            <ProjectMessages 
              projectId={actualProjectId} 
              shareToken={shareToken}
              composeRequest={composeRequest}
              viewerType={(shareInfo?.share_type || '').toString().toLowerCase() === 'trade' ? 'trade' : 'client'}
              viewerEmail={shareInfo?.recipient_email || JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}')?.email || null}
              viewerName={shareInfo?.recipient_name || JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}')?.fullName || null}
              projectLeadName={designerInfo?.name || null}
              onNavigateToCollaborators={() => {
                setActiveTab('collaborators');
                if (invitedCollaborators.length === 0) loadInvitedCollaborators();
              }}
            />
          </div>
        )}

        {activeTab === 'schedule' && !(shareInfo?.share_type === 'trade' && shareInfo?.schedule_access === false) && (
          <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-180px)]">
            {shareToken && shareInfo?.share_type === 'trade' && (
              <div className="px-4 py-3 border-b border-gray-200 bg-orange-50">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-orange-900">
                    Trades: request schedule updates through Project Chat so the designer can approve.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => requestScheduleChangeViaChat('new')}
                      className="px-3 py-2 bg-white border border-orange-300 text-orange-700 rounded-lg text-xs font-semibold hover:bg-orange-100 min-h-[36px]"
                    >
                      Request new date
                    </button>
                    <button
                      type="button"
                      onClick={() => requestScheduleChangeViaChat('change')}
                      className="px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold hover:bg-orange-700 min-h-[36px]"
                    >
                      Request change
                    </button>
                  </div>
                </div>
              </div>
            )}
            <ProjectSchedule projectId={actualProjectId} shareToken={shareToken} />
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-180px)] p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-1">Project Activity</h2>
              <p className="text-sm text-gray-600">
                A chronological feed of all project activity including notes, messages, approvals, and schedule updates. 
                This gives you a complete overview of what's happening on the project.
              </p>
            </div>
            {loadingActivity ? (
              <div className="text-center text-gray-500 py-8">Loading activity...</div>
            ) : activity.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No activity yet</p>
                <p className="text-sm mt-1">Activity will appear here as the project progresses</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activity.map((item) => (
                  <div key={item.id} className="border-l-4 border-green-500 pl-4 py-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.user_name || 'System'}</p>
                        <p className="text-sm text-gray-600">{item.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(item.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.activity_type === 'comment_added' ? 'bg-blue-100 text-blue-700' :
                        item.activity_type === 'trade_note_approved' ? 'bg-green-100 text-green-700' :
                        item.activity_type === 'message' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.activity_type?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'collaborators' && (
          <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-180px)] p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold mb-1">Invited Collaborators</h2>
                <p className="text-sm text-gray-600">
                  Manage who has access to this project. Invite clients and trades, copy their invite links, or remove access.
                </p>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Invite
              </button>
            </div>
            {loadingCollaborators ? (
              <div className="text-center text-gray-500 py-8">Loading collaborators...</div>
            ) : invitedCollaborators.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>No collaborators invited yet</p>
                <p className="text-sm mt-1">Click the "Invite" button above to invite people</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-600">{invitedCollaborators.length} collaborator{invitedCollaborators.length !== 1 ? 's' : ''} invited</span>
                </div>
                {invitedCollaborators.map((collab, idx) => {
                  const isTrade = collab.type === 'trade' || collab.trade_type || collab.share_type === 'trade';
                  
                  return (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{collab.recipient_name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            !isTrade ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {!isTrade ? 'Client' : collab.trade_type || 'Trade'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{collab.recipient_email}</p>
                        {collab.created_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Invited {new Date(collab.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => {
                            const shareToken = collab.shareToken;
                            const link = isTrade
                              ? `${window.location.origin}/shared/${shareToken}/communication`
                              : `${window.location.origin}/shared/${shareToken}`;
                            const projectName = project?.project_name || project?.name || 'this project';
                            const subject = encodeURIComponent(`Invitation to collaborate on ${projectName}`);
                            const body = encodeURIComponent(
                              `Hi ${collab.recipient_name},\n\n` +
                              `You have been invited to collaborate on the project "${projectName}".\n\n` +
                              `Please click the link below to access the project:\n\n` +
                              `${link}\n\n` +
                              `Looking forward to working with you!`
                            );
                            const mailtoLink = `mailto:${collab.recipient_email}?subject=${subject}&body=${body}`;
                            window.location.href = mailtoLink;
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                          title="Resend invitation via email"
                        >
                          Resend Invite
                        </button>
                        <button
                          onClick={() => handleDeleteCollaborator(collab)}
                          className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                          title="Remove collaborator"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    {/* Message Visibility Control */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Message Visibility:
                      </label>
                      <select
                        value={collab.message_visibility || 'all'}
                        onChange={(e) => handleUpdateMessageVisibility(collab, e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      >
                        <option value="all">All Messages</option>
                        <option value="selected">Selected Conversations</option>
                        <option value="none">No Past Messages</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {collab.message_visibility === 'all' && 'Can see all past and future messages'}
                        {collab.message_visibility === 'selected' && 'Can only see messages you grant access to'}
                        {collab.message_visibility === 'none' && 'Can only see new messages after they join'}
                      </p>
                    </div>
                    
                    {/* Schedule Access Control (only for trades) */}
                    {isTrade && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Schedule Access:
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={collab.schedule_access !== undefined ? collab.schedule_access : true}
                            onChange={(e) => handleUpdateScheduleAccess(collab, e.target.checked)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-xs text-gray-700">
                            Allow access to project schedule/calendar
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {collab.schedule_access !== false ? 'Can view and interact with the project schedule' : 'Cannot access the schedule tab'}
                        </p>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-180px)] p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-1">Project Files</h2>
              <p className="text-sm text-gray-600">
                {shareToken
                  ? 'View and download project files. To share something new, attach it in Project Chat.'
                  : 'Upload and manage project files. All collaborators can view and download files.'}
              </p>
            </div>
            <ProjectFiles 
              projectId={actualProjectId} 
              canUpload={!shareToken} 
              shareToken={shareToken}
            />
          </div>
        )}

        {/* Invite Modal - Using Shared Component */}
        {showInviteModal && (
          <InviteCollaboratorModal
            projects={null} // No project selector - using current project
            selectedProjectId={actualProjectId}
            setSelectedProjectId={() => {}} // Not used when projects is null
            defaultProjectId={actualProjectId} // Pre-select current project
            inviteMode={inviteMode}
            setInviteMode={setInviteMode}
            inviteName={inviteName}
            setInviteName={setInviteName}
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            invitePhone={invitePhone}
            setInvitePhone={setInvitePhone}
            notificationMethod={notificationMethod}
            setNotificationMethod={setNotificationMethod}
            inviteType={inviteType}
            setInviteType={setInviteType}
            tradeCategory={tradeCategory}
            setTradeCategory={setTradeCategory}
            messageVisibility={messageVisibility}
            setMessageVisibility={setMessageVisibility}
            scheduleAccess={scheduleAccess}
            setScheduleAccess={setScheduleAccess}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchLocation={searchLocation}
            setSearchLocation={setSearchLocation}
            searchType={searchType}
            setSearchType={setSearchType}
            searchResults={searchResults}
            searching={searching}
            designerLocation={designerLocation}
            onSearch={handleSearchBusinesses}
            onInviteFromSearch={handleInviteFromSearch}
            onAddBusiness={handleAddBusiness}
            onSubmit={handleInvite}
            onClose={() => {
              setShowInviteModal(false);
              setInviteName('');
              setInviteEmail('');
              setInvitePhone('');
              setNotificationMethod('email');
              setInviteType('trade');
              setTradeCategory('contractor');
              setInviteMode('manual');
              setSearchResults([]);
              setSearchQuery('');
              setMessageVisibility('all');
              setScheduleAccess(true);
            }}
          />
        )}
        
        {/* OLD INLINE MODAL - REMOVED - Using shared component above */}
        {false && showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Invite Collaborator</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteName('');
                    setInviteEmail('');
                    setInvitePhone('');
                    setNotificationMethod('email');
                    setInviteType('client');
                    setTradeCategory('contractor');
                    setInviteMode('manual');
                    setSearchResults([]);
                    setSearchQuery('');
                    setMessageVisibility('all');
                    setScheduleAccess(true);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mode Tabs */}
              <div className="flex gap-2 mb-4 border-b">
                <button
                  type="button"
                  onClick={() => setInviteMode('manual')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    inviteMode === 'manual'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Manual Entry
                </button>
                <button
                  type="button"
                  onClick={() => setInviteMode('search')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    inviteMode === 'search'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Search Businesses
                </button>
              </div>

              {/* Manual Entry Form */}
              {inviteMode === 'manual' && (
              <form onSubmit={handleInvite}>
                {/* Email helper banner if email was pre-filled */}
                {inviteEmail && inviteName && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-blue-800 font-medium">✓ Pre-filled from search: {inviteName}</p>
                        <p className="text-xs text-blue-600 mt-1">Please verify the email address before sending</p>
                      </div>
                    </div>
                  </div>
                )}
                
                  <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="client"
                        checked={inviteType === 'client'}
                        onChange={(e) => setInviteType(e.target.value)}
                        className="mr-2"
                      />
                      Client
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="trade"
                        checked={inviteType === 'trade'}
                        onChange={(e) => setInviteType(e.target.value)}
                        className="mr-2"
                      />
                      Trade/Supplier
                    </label>
                  </div>
                </div>

                {/* Trade Category - only show when trade is selected */}
                {inviteType === 'trade' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-gray-500 text-xs">(optional)</span>
                    </label>
                    <select
                      value={tradeCategory}
                      onChange={(e) => setTradeCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <optgroup label="General">
                        <option value="contractor">General Contractor</option>
                        <option value="supplier">Supplier</option>
                        <option value="installer">Installer</option>
                        <option value="consultant">Consultant</option>
                      </optgroup>
                      <optgroup label="Landscaping & Design">
                        <option value="landscaper">Landscaper</option>
                        <option value="landscape designer">Landscape Designer</option>
                        <option value="landscape architect">Landscape Architect</option>
                        <option value="garden designer">Garden Designer</option>
                        <option value="hardscape">Hardscape</option>
                        <option value="outdoor living">Outdoor Living</option>
                      </optgroup>
                      <optgroup label="Installation & Construction">
                        <option value="irrigation">Irrigation</option>
                        <option value="sprinkler">Sprinkler Installation</option>
                        <option value="drainage">Drainage Contractor</option>
                        <option value="fencing">Fencing Contractor</option>
                        <option value="deck builder">Deck Builder</option>
                        <option value="patio">Patio Contractor</option>
                        <option value="retaining wall">Retaining Wall</option>
                        <option value="paving">Paving Contractor</option>
                        <option value="concrete">Concrete Contractor</option>
                      </optgroup>
                      <optgroup label="Tree & Plant Services">
                        <option value="tree service">Tree Service</option>
                        <option value="arborist">Arborist</option>
                        <option value="tree removal">Tree Removal</option>
                        <option value="tree trimming">Tree Trimming</option>
                        <option value="stump removal">Stump Removal</option>
                        <option value="nursery">Nursery</option>
                        <option value="garden center">Garden Center</option>
                      </optgroup>
                      <optgroup label="Maintenance & Care">
                        <option value="lawn care">Lawn Care</option>
                        <option value="lawn mowing">Lawn Mowing</option>
                        <option value="landscape maintenance">Landscape Maintenance</option>
                        <option value="garden maintenance">Garden Maintenance</option>
                        <option value="fertilizer">Fertilizer Service</option>
                        <option value="pest control">Pest Control</option>
                      </optgroup>
                      <optgroup label="Suppliers & Retail">
                        <option value="nursery">Plant Nursery</option>
                        <option value="garden supply">Garden Supply</option>
                        <option value="mulch">Mulch Supplier</option>
                        <option value="stone">Stone Supplier</option>
                        <option value="gravel">Gravel Supplier</option>
                        <option value="soil">Soil Supplier</option>
                      </optgroup>
                      <optgroup label="Specialty Services">
                        <option value="lighting">Outdoor Lighting</option>
                        <option value="water feature">Water Feature</option>
                        <option value="pond">Pond Installation</option>
                        <option value="fire pit">Fire Pit Installation</option>
                        <option value="outdoor kitchen">Outdoor Kitchen</option>
                        <option value="pool">Pool Contractor</option>
                        <option value="snow removal">Snow Removal</option>
                      </optgroup>
                      <optgroup label="Other Services">
                        <option value="excavation">Excavation</option>
                        <option value="grading">Grading</option>
                        <option value="seeding">Lawn Seeding</option>
                        <option value="sod">Sod Installation</option>
                        <option value="roofing">Roofing</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="painting">Painting</option>
                        <option value="carpentry">Carpentry</option>
                        <option value="masonry">Masonry</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the category that best describes this trade or supplier
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Enter name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Visibility
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Control what messages this person can see when they join
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="all"
                        checked={messageVisibility === 'all'}
                        onChange={(e) => setMessageVisibility(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">All Messages</span>
                        <p className="text-xs text-gray-500">Can see all past and future messages in the project</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="selected"
                        checked={messageVisibility === 'selected'}
                        onChange={(e) => setMessageVisibility(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">Selected Conversations</span>
                        <p className="text-xs text-gray-500">Can only see messages you specifically grant access to</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="none"
                        checked={messageVisibility === 'none'}
                        onChange={(e) => setMessageVisibility(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">No Past Messages</span>
                        <p className="text-xs text-gray-500">Can only see new messages sent after they join</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Schedule Access (only for trades) */}
                {inviteType === 'trade' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Access
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={scheduleAccess}
                        onChange={(e) => setScheduleAccess(e.target.checked)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Allow access to project schedule/calendar
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {scheduleAccess ? 'Can view and interact with the project schedule' : 'Cannot access the schedule tab'}
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteName('');
                      setInviteEmail('');
                      setMessageVisibility('all');
                      setScheduleAccess(true);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Send Invite
                  </button>
                </div>
              </form>
              )}

              {/* Business Search */}
              {inviteMode === 'search' && (
                <div>
                  <form onSubmit={handleSearchBusinesses} className="mb-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search for Business
                      </label>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="e.g., landscaper, irrigation, tree service"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={searchLocation}
                        onChange={(e) => setSearchLocation(e.target.value)}
                        placeholder="City, State or leave blank for broader search"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      {designerLocation && (
                        <p className="text-xs text-gray-500 mt-1">
                          Default: {designerLocation} (from your profile)
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Type
                      </label>
                      <select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="all">All Types</option>
                        <optgroup label="Landscaping & Design">
                          <option value="landscaper">Landscaper</option>
                          <option value="landscape designer">Landscape Designer</option>
                          <option value="landscape architect">Landscape Architect</option>
                          <option value="garden designer">Garden Designer</option>
                          <option value="hardscape">Hardscape</option>
                          <option value="outdoor living">Outdoor Living</option>
                        </optgroup>
                        <optgroup label="Installation & Construction">
                          <option value="irrigation">Irrigation</option>
                          <option value="sprinkler">Sprinkler Installation</option>
                          <option value="drainage">Drainage Contractor</option>
                          <option value="fencing">Fencing Contractor</option>
                          <option value="deck builder">Deck Builder</option>
                          <option value="patio">Patio Contractor</option>
                          <option value="retaining wall">Retaining Wall</option>
                          <option value="paving">Paving Contractor</option>
                        </optgroup>
                        <optgroup label="Tree & Plant Services">
                          <option value="tree service">Tree Service</option>
                          <option value="arborist">Arborist</option>
                          <option value="tree removal">Tree Removal</option>
                          <option value="tree trimming">Tree Trimming</option>
                          <option value="stump removal">Stump Removal</option>
                          <option value="nursery">Nursery</option>
                          <option value="garden center">Garden Center</option>
                        </optgroup>
                        <optgroup label="Maintenance & Care">
                          <option value="lawn care">Lawn Care</option>
                          <option value="lawn mowing">Lawn Mowing</option>
                          <option value="landscape maintenance">Landscape Maintenance</option>
                          <option value="garden maintenance">Garden Maintenance</option>
                          <option value="fertilizer">Fertilizer Service</option>
                          <option value="pest control">Pest Control</option>
                        </optgroup>
                        <optgroup label="General Contractors">
                          <option value="contractor">General Contractor</option>
                          <option value="construction">Construction</option>
                          <option value="home improvement">Home Improvement</option>
                          <option value="renovation">Renovation Contractor</option>
                        </optgroup>
                        <optgroup label="Specialty Services">
                          <option value="lighting">Outdoor Lighting</option>
                          <option value="water feature">Water Feature</option>
                          <option value="pond">Pond Installation</option>
                          <option value="fire pit">Fire Pit Installation</option>
                          <option value="outdoor kitchen">Outdoor Kitchen</option>
                          <option value="pool">Pool Contractor</option>
                          <option value="snow removal">Snow Removal</option>
                        </optgroup>
                        <optgroup label="Suppliers & Retail">
                          <option value="nursery">Plant Nursery</option>
                          <option value="garden supply">Garden Supply</option>
                          <option value="mulch">Mulch Supplier</option>
                          <option value="stone">Stone Supplier</option>
                          <option value="gravel">Gravel Supplier</option>
                          <option value="soil">Soil Supplier</option>
                        </optgroup>
                        <optgroup label="Other Services">
                          <option value="excavation">Excavation</option>
                          <option value="grading">Grading</option>
                          <option value="seeding">Lawn Seeding</option>
                          <option value="sod">Sod Installation</option>
                          <option value="roofing">Roofing</option>
                          <option value="plumbing">Plumbing</option>
                          <option value="electrical">Electrical</option>
                          <option value="painting">Painting</option>
                          <option value="carpentry">Carpentry</option>
                          <option value="masonry">Masonry</option>
                          <option value="concrete">Concrete Contractor</option>
                        </optgroup>
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={searching}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {searching ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Searching...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          Search
                        </>
                      )}
                    </button>
                  </form>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Found {searchResults.length} business{searchResults.length !== 1 ? 'es' : ''}
                      </h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {searchResults.map((business, idx) => (
                          <div key={idx} className={`p-3 border rounded-lg hover:bg-gray-50 ${
                            business.aiSuggestion ? 'border-yellow-300 bg-yellow-50' : 
                            business.source === 'local_database' ? 'border-blue-300 bg-blue-50' : 
                            business.source === 'designer_database' ? 'border-green-300 bg-green-50' :
                            'border-gray-200'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-medium text-gray-900">{business.name}</h5>
                                  {business.source === 'local_database' && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      Local
                                    </span>
                                  )}
                                  {business.source === 'designer_database' && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                      Designer
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{business.address}</p>
                                {business.phone && (
                                  <p className="text-xs text-gray-500 mt-1">📞 {business.phone}</p>
                                )}
                                {business.email && (
                                  <p className="text-xs text-gray-500 mt-1">✉️ {business.email}</p>
                                )}
                                {business.rating && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    ⭐ {business.rating} ({business.userRatingsTotal} reviews)
                                  </p>
                                )}
                                {business.aiSuggestion && (
                                  <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                                    <p className="font-medium">⚠️ AI Suggestion</p>
                                    <p className="mt-1">{business.note}</p>
                                    {business.likelyServices && (
                                      <p className="mt-1 italic">Services: {business.likelyServices}</p>
                                    )}
                                  </div>
                                )}
                                {business.source === 'local_database' && (
                                  <p className="text-xs text-blue-600 mt-1">✓ Added to local database</p>
                                )}
                                {business.source === 'designer_database' && (
                                  <p className="text-xs text-green-600 mt-1">✓ Registered designer on PlantADay</p>
                                )}
                              </div>
                              <button
                                onClick={() => handleInviteFromSearch(business)}
                                className="ml-3 px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 whitespace-nowrap"
                              >
                                {business.aiSuggestion ? 'Use Name' : 'Invite'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchResults.length === 0 && !searching && searchQuery && (
                    <div className="text-center text-gray-500 py-8">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="font-medium text-gray-700 mb-2">No businesses found</p>
                      <p className="text-sm text-gray-600 mb-1">
                        Google Places only shows businesses listed on Google Maps.
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        If the business isn't on Google Maps, add it manually below.
                      </p>
                      <button
                        onClick={() => {
                          // Pre-fill the business name from search
                          setNewBusiness({
                            ...newBusiness,
                            name: searchQuery
                          });
                          setShowAddBusinessModal(true);
                        }}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        + Add "{searchQuery}" Manually
                      </button>
                    </div>
                  )}

                  {searchResults.length === 0 && !searching && !searchQuery && (
                    <div className="text-center text-gray-500 py-8">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p>Search for businesses to invite</p>
                      <p className="text-sm mt-1 mb-4">Results will appear here</p>
                      <p className="text-xs text-gray-500 mb-4">
                        💡 Tip: Businesses not on Google Maps can be added manually
                      </p>
                      <button
                        onClick={() => setShowAddBusinessModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        + Add Business Manually
                      </button>
                    </div>
                  )}

                  {/* Add Business Button */}
                  {searchResults.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => setShowAddBusinessModal(true)}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Business Not Listed
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Business Modal */}
        {showAddBusinessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Business Manually</h3>
                <button
                  onClick={() => {
                    setShowAddBusinessModal(false);
                    setNewBusiness({
                      name: '',
                      businessType: '',
                      address: '',
                      city: '',
                      provinceState: '',
                      phone: '',
                      email: '',
                      website: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!newBusiness.name.trim()) {
                  alert('Business name is required');
                  return;
                }

                try {
                  const token = localStorage.getItem('authToken');
                  const response = await fetch(`${API_BASE_URL}/google-business/local`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(token && { 'Authorization': `Bearer ${token}` })
                    },
                    body: JSON.stringify(newBusiness)
                  });

                  if (!response.ok) {
                    throw new Error('Failed to add business');
                  }

                  alert('Business added! It will now appear in search results.');
                  setShowAddBusinessModal(false);
                  setNewBusiness({
                    name: '',
                    businessType: '',
                    address: '',
                    city: '',
                    provinceState: '',
                    phone: '',
                    email: '',
                    website: ''
                  });
                  
                  // Refresh search if there was a query
                  if (searchQuery.trim()) {
                    // Call search without event parameter
                    setSearching(true);
                    try {
                      const params = new URLSearchParams({
                        query: searchQuery,
                        type: searchType
                      });
                      
                      if (searchLocation) {
                        params.append('location', searchLocation);
                      }

                      const refreshResponse = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
                        headers: {
                          ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
                        }
                      });

                      if (refreshResponse.ok) {
                        const refreshData = await refreshResponse.json();
                        setSearchResults(refreshData.results || []);
                      }
                    } catch (err) {
                      console.error('Error refreshing search:', err);
                    } finally {
                      setSearching(false);
                    }
                  }
                } catch (error) {
                  alert('Failed to add business. Please try again.');
                  console.error('Error adding business:', error);
                }
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={newBusiness.name}
                      onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                      placeholder="e.g., Aspen Ridge Design"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Type
                    </label>
                    <input
                      type="text"
                      value={newBusiness.businessType}
                      onChange={(e) => setNewBusiness({ ...newBusiness, businessType: e.target.value })}
                      placeholder="e.g., Landscape Designer"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={newBusiness.address}
                      onChange={(e) => setNewBusiness({ ...newBusiness, address: e.target.value })}
                      placeholder="Street address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={newBusiness.city}
                        onChange={(e) => setNewBusiness({ ...newBusiness, city: e.target.value })}
                        placeholder="City"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Province/State
                      </label>
                      <input
                        type="text"
                        value={newBusiness.provinceState}
                        onChange={(e) => setNewBusiness({ ...newBusiness, provinceState: e.target.value })}
                        placeholder="BC"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={newBusiness.phone}
                        onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                        placeholder="(250) 123-4567"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newBusiness.email}
                        onChange={(e) => setNewBusiness({ ...newBusiness, email: e.target.value })}
                        placeholder="contact@business.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={newBusiness.website}
                      onChange={(e) => setNewBusiness({ ...newBusiness, website: e.target.value })}
                      placeholder="https://www.business.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add Business
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddBusinessModal(false);
                      setNewBusiness({
                        name: '',
                        businessType: '',
                        address: '',
                        city: '',
                        provinceState: '',
                        phone: '',
                        email: '',
                        website: ''
                      });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationCentre;

